"use client";

import { useEffect } from "react";
import { useTranslations } from "next-intl";
import { useWatch } from "react-hook-form";
import { Stack } from "@leabankab/component-library";
import {
	ConfirmationButton,
	ConstantMultiSelect,
	FormContainer,
	RadioCardField,
} from "@/components/form";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import {
	applyMockFormDefaults,
	getMockCreditCardCustomerDefaults,
} from "@/dev/console/data/mock-form-values";
import type { CreditCardCustomerForm } from "@/features/customer-information/hooks/use-credit-card-customer-form";
import { useCreditCardCustomerForm } from "@/features/customer-information/hooks/use-credit-card-customer-form";
import { useCustomerInformationSubmit } from "@/features/customer-information/hooks/use-customer-information-submit";
import {
	flattenToKycArray,
	kycArrayToFormPartial,
} from "@/features/customer-information/utils/transformKycArray";
import {
	CreditCardCustomerInformationContext,
	CreditCardCustomerInformationPayload,
} from "../types";

interface Props {
	taskId: string;
	context: CreditCardCustomerInformationContext;
}

const mapFormToBody = (
	form: CreditCardCustomerForm
): CreditCardCustomerInformationPayload => {
	const { isPEP, citizenship, ...kyc } = form;
	return {
		isPEP,
		citizenship,
		kycQuestions: flattenToKycArray(kyc),
	};
};

const mapContextToForm = (
	context: CreditCardCustomerInformationContext,
	mock: boolean
): Partial<CreditCardCustomerForm> => {
	const { citizenships, kycQuestions } = context;

	return applyMockFormDefaults(
		{
			citizenship: citizenships ?? [],
			...kycArrayToFormPartial<CreditCardCustomerForm>(kycQuestions ?? []),
		},
		getMockCreditCardCustomerDefaults(),
		mock
	);
};

const CreditCustomerComponent = ({ context, taskId }: Props) => {
	const t = useTranslations("pages.KnowYourCustomerPage");
	const { mock } = useRuntimeConfig();

	const methods = useCreditCardCustomerForm().useForm;
	const { getValues, handleSubmit, reset, control } = methods;
	const useCardForTransactions = useWatch({
		control,
		name: "useCardForTransactions",
	});

	const submit =
		useCustomerInformationSubmit<CreditCardCustomerInformationPayload>({
			taskId,
			processType: context.process,
		});

	useEffect(() => {
		if (!context || !reset) return;
		reset(mapContextToForm(context, mock));
	}, [context, reset, mock]);

	const onSubmit = handleSubmit(() => submit(mapFormToBody(getValues())));

	const booleanOptions = [
		{ value: true, label: t("binaryResponse.affirmativeOptionText") },
		{ value: false, label: t("binaryResponse.opposingOptionText") },
	];

	return (
		<FormContainer
			methods={methods}
			intro={t("text.info")}
			onSubmit={onSubmit}
			getPayloadPreview={mapFormToBody}
		>
			<Stack gap={2.5}>
				<ConstantMultiSelect
					name="citizenship"
					label={t("selectInput.labels.citizenship")}
					optionsKey="countries"
					min={0}
				/>

				<ConstantMultiSelect
					name="expectedUseOfCard"
					label={t("selectInput.labels.expectedUseOfCard")}
					optionsKey="expectedUseOfCardOptions"
					min={0}
				/>

				<ConstantMultiSelect
					name="expectedLocationUseOfCard"
					label={t("selectInput.labels.expectedLocationUseOfCard")}
					optionsKey="expectedLocationUseOfCardOptions"
					min={0}
				/>

				<ConstantMultiSelect
					name="howToPayForInvoices"
					label={t("selectInput.labels.howToPayForInvoices")}
					optionsKey="howToPayForInvoicesOptions"
					min={0}
				/>
			</Stack>

			<Stack gap={2.5}>
				<RadioCardField
					name="useCardForTransactions"
					label={t("binaryResponse.useCardForTransactionsLabel")}
					options={booleanOptions}
					className="pt-1"
				/>
				{useCardForTransactions && (
					<ConstantMultiSelect
						name="useCardForTransactionsLocation"
						label={t("selectInput.labels.useCardForTransactionsLocation")}
						optionsKey="expectedLocationUseOfCardOptions"
						min={0}
					/>
				)}

				<RadioCardField
					name="withdrawMoreThan50k"
					label={t("binaryResponse.withdrawMoreThan50kLabel")}
					options={booleanOptions}
					className="pt-1"
				/>

				<RadioCardField
					name="isPEP"
					label={t("binaryResponse.pepLabel")}
					tooltip={t("tooltip.infoText")}
					options={booleanOptions}
					className="pt-1"
				/>
			</Stack>

			<ConfirmationButton label={t("button.label")} type="submit" />
		</FormContainer>
	);
};

export default CreditCustomerComponent;
